// Libraries
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, Link, useLocation } from "react-router-dom";
import ReactHtmlParser from "react-html-parser";
import moment from "moment";
import axios from "axios";

// Components
import AdHelper from "../shared/ad/AdHelper";
import config from "../../config";

// Ant Design Components
import {
  Button,
  Row,
  Col,
  BackTop,
  Input,
  message,
  Avatar,
  Spin,
  Popover,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  SendOutlined,
  CheckOutlined,
  CloseOutlined,
  UserOutlined,
} from "@ant-design/icons";

// ---------- localStorage safe helpers ----------
const safeJSONParse = (value, fallback = null) => {
  try {
    if (!value || value === "undefined" || value === "null") return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const safeGetItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn(`localStorage.getItem failed for key="${key}"`, e);
    return null;
  }
};

const NewsPost = (props) => {
  const [comment, setComment] = useState("");
  const [candidatesData, setCandidatesData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ Local post state so UI updates immediately after vote/comment
  const [postData, setPostData] = useState(props?.data || {});

  // Keep postData in sync if parent passes new data (rare, but safe)
  useEffect(() => {
    if (props?.data?._id && props.data._id !== postData?._id) {
      setPostData(props.data);
    } else if (props?.data && postData?._id === props.data._id) {
      // if same post id but parent updated it
      setPostData(props.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.data]);

  const location = useLocation();
  const history = useHistory();

  const backToAccountTypesOptions = () => {
    history.push({
      pathname: "/news",
      state: {
        category: location.state?.fromCategory,
        filters: location.state?.fromFilters,
        scrollY: location.state?.fromScrollY,
      },
    });
  };

  // ✅ Safe user parse (prevents crashes if localStorage is corrupted)
  const user = useMemo(
    () => safeJSONParse(safeGetItem("user"), null), // ✅ Safe nested call
    []
  );

  const isLoggedIn = Boolean(user && user._id);
  const canCommentVote = isLoggedIn;

  // ✅ If not logged in, these should be false
  const userUpVoted = isLoggedIn
    ? (postData?.upvotes || []).includes(user._id)
    : false;

  const userDownVoted = isLoggedIn
    ? (postData?.downvotes || []).includes(user._id)
    : false;

  const upVoteCount = (postData?.upvotes?.length || 0).toLocaleString("en-US");
  const downVoteCount = (postData?.downvotes?.length || 0).toLocaleString("en-US");

  // block if they already voted either way
  const canVote = userUpVoted || userDownVoted;

  // Fetch candidates for avatars
  useEffect(() => {
    let mounted = true;

    async function getCandidatesData() {
      try {
        const ids = Array.isArray(postData?.candidates) ? postData.candidates : [];
        if (ids.length === 0) {
          if (mounted) setCandidatesData([]);
          return;
        }

        const accessToken = safeGetItem("access_token"); // ✅ Safe access

        let headers = {
          ids: JSON.stringify(ids),
          token: accessToken,
        };

        const { data: candidateResultData } = await axios.get(
          `${config.CANDIDATES}/candidates/find-by-ids`,
          { headers }
        );

        if (mounted) {
          setCandidatesData(Array.isArray(candidateResultData) ? candidateResultData : []);
        }
      } catch (e) {
        console.log(e);
        if (mounted) setCandidatesData([]);
      }
    }

    getCandidatesData();

    return () => {
      mounted = false;
    };
  }, [postData?.candidates]);

  // ✅ Patch + update locally (no refetch)
  async function postUpdatePost(id, updatedPost, successMessage) {
    try {
      const accessToken = safeGetItem("access_token"); // ✅ Safe access

      await axios.patch(`${config.BLOGS}/blogs/update/${id}`, updatedPost, {
        headers: {
          token: accessToken,
        },
      });

      // ✅ Update UI immediately
      setPostData(updatedPost);

      // ✅ If parent wants to update NewsPage list locally, let it
      props.onPostUpdated?.(updatedPost);

      // Optional legacy refetch if you still pass it
      props.getData?.();

      message.success(successMessage);
    } catch (err) {
      console.log(err);
      message.warning("Something went wrong‼️");
    }
  }

  // Add comment
  async function addCommentToPost(event) {
    event.preventDefault();

    if (!isLoggedIn) {
      message.info("Please login to use this feature.");
      return;
    }

    if (!comment || !comment.trim()) return;

    setLoading(true);

    const newComment = {
      comment: comment.trim(),
      _id: user._id,
      nickname: user.nickname || user.name || "User",
      picture: user.picture || "",
    };

    const updatedPost = {
      ...postData,
      comments: [newComment, ...(postData.comments || [])],
    };

    await postUpdatePost(postData._id, updatedPost, "Comment posted!");
    setComment("");
    setLoading(false);
  }

  // Vote
  async function voteInPost(event, type) {
    event.preventDefault();

    if (!isLoggedIn) {
      message.info("Please login to use this feature.");
      return;
    }

    if (canVote) {
      message.info("You have voted already!");
      return;
    }

    const updatedPost = {
      ...postData,
      upvotes:
        type === "upvote"
          ? [user._id, ...(postData.upvotes || [])]
          : (postData.upvotes || []),
      downvotes:
        type === "downvote"
          ? [user._id, ...(postData.downvotes || [])]
          : (postData.downvotes || []),
    };

    await postUpdatePost(postData._id, updatedPost, "Voted Successfully!");
  }

  const backButton = (
    <Button
      className="btn-bordered"
      onClick={backToAccountTypesOptions}
      style={{ width: "96px" }}
      icon={<ArrowLeftOutlined />}
      size="large"
    >
      Back
    </Button>
  );

  const popoverContent = !canCommentVote
    ? "Please signup in order to vote"
    : canVote
    ? "You have voted!"
    : "";

  const PopoverWrapper = ({ children }) => {
    return (
      <Popover
        content={popoverContent}
        trigger={!canCommentVote || canVote ? "hover" : undefined}
      >
        {children}
      </Popover>
    );
  };

  return (
    <div className="BlogPost">
      <div className="constrain">
        <header>
          <div className="header-buttons">
            {backButton}

            {/* Admin condition: only show btn if admin */}
            {user?.role === "admin" && (
              <Link to={`${postData._id}/edit`} className="link">
                <Button className="btn-solid" size="large" icon={<EditOutlined />}>
                  Edit Article
                </Button>
              </Link>
            )}
          </div>

          <h2>{postData.title}</h2>
        </header>

        <Row>
          <Col xs={0} xl={3}>
            <AdHelper page="news-post" position="left" />
          </Col>

          <Col xs={24} xl={18}>
            {postData.coverImage ? (
              <div className="news-cover-image">
                <img src={postData.coverImage} alt="news-cover" />
              </div>
            ) : null}

            {postData.videoUrl ? (
              <div className="videoContainer">
                <div className="videoWrapper">
                  <iframe
                    src={postData.videoUrl}
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    title="video"
                  />
                </div>
              </div>
            ) : null}

            <div className="meta">
              <h3>
                Published by{" "}
                <span>
                  <span className="nickname">{postData.author}</span>
                </span>
                <br />
                {postData.created_at ? (
                  <span className="date">
                    {moment(postData.created_at).format("MMMM DD, YYYY")}
                  </span>
                ) : null}
                <br />
                <span className="date">{postData.city}</span>
              </h3>
            </div>

            {postData.editorial && (
              <div id="post-editorial">
                <h4>Editorial / NoCap Commentary</h4>
                {ReactHtmlParser(postData.editorial)}
              </div>
            )}

            <div id="post-body">
              {ReactHtmlParser(postData.body || "")}

              <div className="BlogPostVoteCommentsSection" style={{ marginTop: 8 }}>
                <div className="title">
                  <h3>No Cap!</h3>
                </div>

                {postData.state && (
                  <div style={{ display: "flex" }}>
                    <div style={{ display: "inline-block", marginRight: "10px" }}>
                      <h4>State:</h4>
                    </div>
                    <div style={{ display: "inline-block" }}>
                      <p>{postData.state}</p>
                    </div>
                  </div>
                )}

                {postData.district && (
                  <div style={{ display: "flex" }}>
                    <div style={{ display: "inline-block", marginRight: "10px" }}>
                      <h4>District:</h4>
                    </div>
                    <div style={{ display: "inline-block" }}>
                      <p>{postData.district}</p>
                    </div>
                  </div>
                )}

                <div>
                  <div className="title">
                    <h4>Fact Check</h4>
                  </div>

                  <div className="candidateAvatars" style={{ marginBottom: 16 }}>
                    {Array.isArray(postData.factCheck) && postData.factCheck.length > 0
                      ? postData.factCheck.map((article) => (
                          <div key={article.link} style={{ display: "flex" }}>
                            <div style={{ display: "inline-block", marginRight: "10px" }}>
                              <a
                                href={article.link}
                                target="_blank"
                                rel="noreferrer"
                                style={{ display: "flex", alignItems: "center", gap: 10 }}
                              >
                                <Avatar
                                  size="large"
                                  src={article.imageLink}
                                  icon={!article.imageLink ? <UserOutlined /> : undefined}
                                />
                                <div style={{ display: "inline-block" }}>
                                  <p style={{ margin: 0 }}>{article.newsPage}</p>
                                </div>
                              </a>
                            </div>
                          </div>
                        ))
                      : <div style={{ opacity: 0.7 }}>No fact checks provided.</div>}
                  </div>
                </div>

                <div>
                  <div className="title">
                    <h4>Candidates in this news</h4>
                  </div>

                  <div className="candidateAvatars" style={{ marginBottom: 16 }}>
                    {(candidatesData || []).map((candidate) => (
                      <Avatar
                        key={candidate?._id || candidate?.bio?.name}
                        size="small"
                        src={candidate?.bio?.profileImage}
                        icon={!candidate?.bio?.profileImage && <UserOutlined />}
                      />
                    ))}
                  </div>
                </div>

                {/* Vote Actions */}
                <div style={{ display: "flex", marginBottom: 16 }}>
                  <PopoverWrapper>
                    <Button
                      className="actionButton flex-row align-center"
                      onClick={(e) => voteInPost(e, "upvote")}
                      size="large"
                      icon={<CheckOutlined />}
                      disabled={!isLoggedIn || canVote}
                    >
                      <div className="actionButtonText">
                        <span className="title">Upvotes</span>
                        <span className="body">{upVoteCount}</span>
                      </div>
                    </Button>
                  </PopoverWrapper>

                  <PopoverWrapper>
                    <Button
                      className="actionButton flex-row align-center"
                      onClick={(e) => voteInPost(e, "downvote")}
                      size="large"
                      icon={<CloseOutlined />}
                      disabled={!isLoggedIn || canVote}
                    >
                      <div className="actionButtonText">
                        <div className="title">Downvotes</div>
                        <div className="body">{downVoteCount}</div>
                      </div>
                    </Button>
                  </PopoverWrapper>
                </div>

                {/* Comments */}
                <div className="commentsBody">
                  <h3>Comments</h3>
                  {(postData.comments || []).map((c, idx) => (
                    <div className="body" key={idx}>
                      <strong>{c.nickname}</strong> {c.comment}
                    </div>
                  ))}
                </div>

                {canCommentVote && (
                  <Input.Group
                    className="commentsInputContainer"
                    compact
                    style={{ display: "flex", justifyContent: "space-around" }}
                  >
                    <Input
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Type to add comment"
                      disabled={loading}
                      bordered={false}
                    />
                    <Button
                      size="large"
                      icon={loading ? <Spin /> : <SendOutlined />}
                      disabled={loading || !comment?.trim()}
                      onClick={(e) => addCommentToPost(e)}
                    />
                  </Input.Group>
                )}
              </div>
            </div>
          </Col>

          <Col xs={0} xl={3}>
            <AdHelper page="news-post" position="right" />
          </Col>

          <Col xs={12} xl={0}>
            <AdHelper page="news-post" position="left" />
          </Col>
          <Col xs={12} xl={0}>
            <AdHelper page="news-post" position="right" />
          </Col>
        </Row>
      </div>

      <div className="footer" style={{ justifyContent: "center" }}>
        {backButton}
      </div>

      <BackTop />
    </div>
  );
};

export default NewsPost;